import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Coffee, Download, Share, Plus, X, Compass, Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';

const DISMISS_KEY = 'wcn-install-dismissed';

function isStandalone() {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    // iOS Safari exposes this legacy flag when launched from the home screen.
    window.navigator.standalone === true
  );
}

function isIos() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent);
}

/**
 * Which iOS browser is this? On iOS, "Add to Home Screen" installs a real
 * web app only from Safari — every other iOS browser (and every in-app
 * webview) is a WebKit wrapper that cannot do it. So we have to tell the two
 * apart and say so, instead of showing instructions that will not work.
 *
 * Returns `null` in Safari, otherwise a display name for the browser in use.
 */
function detectIosNonSafariBrowser() {
  const ua = window.navigator.userAgent;
  const matchers = [
    [/CriOS/i, 'Chrome'],
    [/FxiOS/i, 'Firefox'],
    [/EdgiOS/i, 'Edge'],
    [/OPiOS|OPT\//i, 'Opera'],
    [/DuckDuckGo/i, 'DuckDuckGo'],
    [/YaBrowser/i, 'Yandex'],
    [/Instagram/i, 'the Instagram browser'],
    [/FBAN|FBAV|FB_IAB/i, 'the Facebook browser'],
    [/Line\//i, 'the LINE browser'],
    [/Twitter/i, 'the X browser'],
    [/GSA\//i, 'the Google app'],
  ];
  for (const [pattern, name] of matchers) {
    if (pattern.test(ua)) return name;
  }
  // Safari always ships both a `Version/` and a `Safari/` token. An in-app
  // webview (SFSafariViewController excepted) usually has neither.
  if (/Safari/i.test(ua) && /Version\//i.test(ua)) return null;
  return 'this browser';
}

/**
 * A dismissible "Add to Home Screen" banner.
 *
 * On Chrome/Edge/Android it uses the captured `beforeinstallprompt` event to
 * trigger the native install flow. On iOS — which has no such event — it
 * explains the manual Share → "Add to Home Screen" flow, and, when the page is
 * open in a non-Safari iOS browser, says plainly that the feature is available
 * only in Safari and offers the page link to copy across. It stays hidden once
 * installed or once the user dismisses it.
 */
export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIosHint, setShowIosHint] = useState(false);
  const [iosBrowser, setIosBrowser] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    if (localStorage.getItem(DISMISS_KEY) === '1') return;

    const onBeforeInstall = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);

    const onInstalled = () => {
      setVisible(false);
      setDeferredPrompt(null);
    };
    window.addEventListener('appinstalled', onInstalled);

    // iOS never fires `beforeinstallprompt`, so surface the manual hint there.
    let iosTimer;
    if (isIos()) {
      const nonSafari = detectIosNonSafariBrowser();
      setIosBrowser(nonSafari);
      // Outside Safari there is no button to press, so lead with the
      // explanation rather than making the user tap to find out.
      if (nonSafari) setShowIosHint(true);
      iosTimer = window.setTimeout(() => setVisible(true), 1500);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
      if (iosTimer) window.clearTimeout(iosTimer);
    };
  }, []);

  const dismiss = () => {
    setVisible(false);
    setShowIosHint(false);
    localStorage.setItem(DISMISS_KEY, '1');
  };

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      setDeferredPrompt(null);
      setVisible(false);
      if (outcome !== 'accepted') {
        // If they dismissed the native dialog, don't nag again.
        localStorage.setItem(DISMISS_KEY, '1');
      }
      return;
    }
    // iOS: reveal the manual instructions.
    setShowIosHint(true);
  };

  const copyLink = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access can be blocked; the address bar is the fallback.
      window.prompt('Copy this link and open it in Safari:', url);
    }
  };

  const isIosNonSafari = Boolean(iosBrowser);

  const description = isIosNonSafari
    ? `Adding this app to your Home Screen works only in Safari on iPhone and iPad. ${iosBrowser === 'this browser' ? 'This browser' : iosBrowser} cannot do it.`
    : 'Install Signature Coffee Note for a faster, full-screen app-like experience.';

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 24 }}
          transition={{ duration: 0.25, ease: 'easeOut' }}
          className="fixed inset-x-0 bottom-0 z-50 px-3 pb-[calc(env(safe-area-inset-bottom)+0.75rem)] pointer-events-none"
        >
          <div className="pointer-events-auto mx-auto flex max-w-md flex-col gap-3 rounded-2xl border border-border bg-card/95 p-4 shadow-lg backdrop-blur">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                {isIosNonSafari ? <Compass size={20} strokeWidth={2} /> : <Coffee size={20} strokeWidth={2} />}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-foreground">
                  {isIosNonSafari ? 'Open in Safari to add to Home Screen' : 'Add to Home Screen'}
                </p>
                <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{description}</p>
              </div>
              <button
                type="button"
                onClick={dismiss}
                aria-label="Dismiss"
                className="-mr-1 -mt-1 shrink-0 rounded-lg p-1 text-muted-foreground transition-colors hover:text-foreground"
              >
                <X size={18} />
              </button>
            </div>

            {showIosHint ? (
              <div className="flex flex-col gap-2 rounded-xl bg-muted px-3 py-2.5">
                <p className="text-xs font-medium text-foreground">
                  {isIosNonSafari ? 'How to install in 4 steps' : 'How to install in 3 steps'}
                </p>
                <ol className="list-inside list-decimal space-y-1 text-xs leading-relaxed text-muted-foreground marker:font-medium marker:text-foreground">
                  {isIosNonSafari && (
                    <li>
                      Open this page in <span className="font-medium text-foreground">Safari</span> — the blue compass
                      icon on your Home Screen.
                    </li>
                  )}
                  <li>
                    Tap the <Share size={13} className="inline align-[-2px] text-primary" />{' '}
                    <span className="font-medium text-foreground">Share</span> button in Safari&apos;s toolbar.
                  </li>
                  <li>
                    Scroll down and choose <Plus size={13} className="inline align-[-2px] text-primary" />{' '}
                    <span className="font-medium text-foreground">Add to Home Screen</span>.
                  </li>
                  <li>
                    Tap <span className="font-medium text-foreground">Add</span> to finish.
                  </li>
                </ol>
                <p className="text-[11px] leading-relaxed text-muted-foreground">
                  Add to Home Screen is a Safari-only feature on iPhone and iPad. Other browsers and in-app browsers
                  cannot install this app.
                </p>
                {isIosNonSafari && (
                  <Button size="sm" variant="outline" className="mt-0.5 w-full gap-2" onClick={copyLink}>
                    {copied ? <Check size={16} /> : <Copy size={16} />}
                    {copied ? 'Link copied' : 'Copy link for Safari'}
                  </Button>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Button size="sm" className="flex-1 gap-2" onClick={handleInstall}>
                  <Download size={16} />
                  {deferredPrompt ? 'Install' : 'How to install'}
                </Button>
                <Button size="sm" variant="ghost" onClick={dismiss}>
                  Not now
                </Button>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
